import { FC, ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import logo from "../../../assets/images/landing/logo.png";
import { useTranslation } from "../../../hooks/useTranslation";
import ContactUsModal from "../ContactUsModal/ContactUsModal";
import { isPublicNext } from "../../../next/public/mode";
import { LegacyWayBack } from "../../../next/public/LegacyWayBack";
import type { PublicIconName } from "../../../next/public/PublicIcon";
import { PublicFrame } from "../../../next/public/PublicFrame";
import "./styles.scss";

interface Props {
  /** Small uppercase label above the title, e.g. "WELCOME BACK". */
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  /** Route the back chevron points at. Omit to hide it (the true entry screens). */
  backTo?: string;
  /** Small print at the bottom of the brand panel/hero. Omit for none. */
  footnote?: ReactNode;
  /** Rendered under the content, e.g. a "Sign in instead" line or language pills. */
  footer?: ReactNode;
  /** The header help icon, which opens the shared Contact Us modal. Defaults
   * to shown — every one of these pages wants it — pass `false` to hide it. */
  help?: boolean;
  /** An illustration for the brand panel (desktop only). */
  art?: string;
  /** A version of `art` drawn for the gradient panel (white shapes). Without it
   *  the panel shows `art` itself. */
  brandArt?: string;
  /** Text beside the back arrow, e.g. "Back to home". Omit for the bare icon. */
  backLabel?: string;
  /** The product's front door (Welcome): heading centred, roomier spacing, and on
   *  a phone the content centred in the space under the hero. */
  entry?: boolean;
  /** New look: one functional icon above the title (route, login, link, key, mail-check, unlock). */
  icon?: PublicIconName;
}

/**
 * The shared shell for every signed-out auth page (Login, Welcome, Forgot/Reset
 * Password) — same visual language as /registration's card (brand panel +
 * gradient, eyebrow/title/subtitle, white content pane), built as its own
 * component rather than importing OnboardingPreview's `Shell`/styles.scss.
 * See styles.scss's header comment for why: those classes are deliberately
 * scoped to the onboarding chunk, and Login/Welcome (main bundle) vs Forgot/
 * Reset Password (their own separate lazy chunks) are exactly the shape of
 * import graph that class-sharing bug needs.
 */
/** The switchboard line for the public look (next/public/mode.ts): the new frame carries the same props; today's markup is below. */
export const AuthShell: FC<Props> = (props) =>
  isPublicNext() ? (
    <PublicFrame eyebrow={props.eyebrow} title={props.title} subtitle={props.subtitle} backTo={props.backTo} backLabel={props.backLabel}
      footnote={props.footnote} footer={props.footer} entry={props.entry} icon={props.icon}>{props.children}</PublicFrame>
  ) : <LegacyAuthShell {...props} />;

const LegacyAuthShell: FC<Props> = ({
  eyebrow, title, subtitle, children, backTo, footnote, footer, help = true, art, brandArt, backLabel, entry,
}) => {
  const { t } = useTranslation();
  const tagline = t("onboarding.tagline");
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <div className="authshell-stage">
      <div className={"authshell-card" + (entry ? " authshell-card--entry" : "")}>
        <aside className="authshell-brand" aria-hidden="true">
          <div>
            <img className="authshell-brand__logo" src={logo} alt="" />
            <p className="authshell-brand__tagline">{tagline}</p>
          </div>
          {(brandArt || art) && <img className="authshell-brand__art" src={brandArt || art} alt="" />}
          {footnote && <p className="authshell-brand__foot">{footnote}</p>}
        </aside>

        <div className="authshell-pane">
          <div className="authshell-hero" aria-hidden="true">
            <img className="authshell-hero__logo" src={logo} alt="ConnectYid" />
            <p className="authshell-hero__tagline">{tagline}</p>
          </div>

          {backTo && (
            <Link
              to={backTo}
              className={"authshell-back" + (backLabel ? " authshell-back--labeled" : "")}
              aria-label={backLabel ? undefined : t("onboarding.go_back")}
            >
              <ArrowBackRoundedIcon fontSize="small" />
              {backLabel && <span>{backLabel}</span>}
            </Link>
          )}
          {/* One button, styled per breakpoint in styles.scss: white-on-gradient
              over the mobile hero, plain blue over the desktop white pane. */}
          {help && (
            <button
              type="button"
              className="authshell-help"
              onClick={() => setHelpOpen(true)}
              aria-label={t("onboarding.contact.open")}
              title={t("onboarding.contact.open")}
            >
              <SupportAgentRoundedIcon fontSize="small" />
            </button>
          )}

          <div className="authshell-content">
            <div className="authshell-head">
              {eyebrow && <p className="authshell-eyebrow">{eyebrow}</p>}
              <h1 className="authshell-title">{title}</h1>
              {subtitle && <p className="authshell-sub">{subtitle}</p>}
            </div>

            <div className="authshell-body">{children}</div>

            {footer && <div className="authshell-footer">{footer}</div>}
            <LegacyWayBack />

          </div>
        </div>
      </div>
      {help && <ContactUsModal open={helpOpen} onClose={() => setHelpOpen(false)} />}
    </div>
  );
};
